import type { ComponentType, SVGProps } from 'react'

/**
 * Tamanho e espessura únicos de toda a iconografia. O sistema de design proíbe
 * misturar estilos de ícone, e a forma de garantir isso é não deixar cada ponto
 * de uso escolher: os componentes de `icones/` já saem com traço e viewBox
 * fechados, e só o tamanho é ajustável.
 */
export const TAMANHO_ICONE = 16
export const TRACO_ICONE = 1.75

export interface PropsIcone {
  /** Lado do quadrado do ícone em px. Padrão 16. */
  tamanho?: number
  /** Sinônimo de tamanho para compatibilidade com bibliotecas e chamadas size={...}. */
  size?: number
  className?: string
  'aria-hidden'?: boolean | 'true' | 'false'
}

type Origem = ComponentType<SVGProps<SVGSVGElement> & { size?: number; strokeWidth?: number }>

/**
 * Embrulha um ícone da biblioteca fixando traço e viewBox.
 *
 * `aria-hidden` é o padrão porque ícone em botão é reforço visual: quem nomeia o
 * comando é o texto ao lado ou o `aria-label` do botão. Ícone anunciado junto com
 * o rótulo faz o leitor de tela repetir a mesma informação duas vezes.
 */
export function comEstiloPadrao(Origem: Origem, nome: string) {
  function Icone({ tamanho, size, className, 'aria-hidden': ariaHidden = true }: PropsIcone) {
    const dimensao = size ?? tamanho ?? TAMANHO_ICONE
    return (
      <Origem
        size={dimensao}
        strokeWidth={TRACO_ICONE}
        className={className ? `icone ${className}` : 'icone'}
        aria-hidden={ariaHidden}
        focusable="false"
      />
    )
  }
  Icone.displayName = `Icone${nome}`
  return Icone
}

/**
 * Casca dos ícones próprios do domínio — calço, cunha, prancha e afins, que
 * nenhuma biblioteca genérica tem. Mesmo viewBox, mesmo traço, mesmas pontas
 * arredondadas dos ícones da biblioteca, para que não se distingam em uso.
 */
export function IconeProprio({
  tamanho,
  size,
  className,
  'aria-hidden': ariaHidden = true,
  children,
}: PropsIcone & { children: React.ReactNode }) {
  const dimensao = size ?? tamanho ?? TAMANHO_ICONE
  return (
    <svg
      width={dimensao}
      height={dimensao}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={TRACO_ICONE}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ? `icone ${className}` : 'icone'}
      aria-hidden={ariaHidden}
      focusable="false"
    >
      {children}
    </svg>
  )
}
